import React, { useState, useCallback } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { TextField, mobile } from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';

import { Header } from '../../../styles/header';

const FIELD_REQUIRED = 'FIELD_REQUIRED';
const FIELD_INVALID = 'FIELD_INVALID';

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const FieldWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;

  ${mobile`
    flex-direction: column;
  `}
`;

const FieldSubText = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizes.lg}px;

  ${mobile`
    flex-direction: column;
  `}
`;

const Title = styled.span`
  ${({ isRequired, theme }) =>
    isRequired &&
    css`
      ::after {
        content: '*';
        color: ${theme.colors.textDefault};
      }
    `}
`;

const MaximumCharacterMsg = styled.p`
  margin-top: 5px;
  margin-bottom: 10px;
  font-size: 12px;
  line-height: 1.5;
`;

const StyledTextField = styled(TextField)`
  width: 50%;

  > fieldset {
    border-radius: ${({ theme }) => theme.borderRadiusTextArea};
    margin-bottom: 0px;
  }

  & textarea {
    min-height: 200px;
    font-weight: normal;
    font-size: 16px;
    line-height: 21px;
    resize: vertical;
  }

  ${mobile`
    width: 100%;
  `}
`;

const InstructionsList = styled.ul`
  margin-left: 12px;
  list-style-type: disc;
  width: 100%;
`;

const InstructionsItem = styled.li`
  ${({ theme }) => theme.typography.variants.bodyMed.textStyle}
  margin: 0px 0px;
`;

const OptionalHeaderWrapper = styled.div`
  padding-top: 28px;
  padding-bottom: 10px;
  display: none;
`;

const OptionalHeader = styled(Header).attrs({
  level: 1,
})`
  font-size: 16px;
  text-align: left;
  color: ${({ theme }) => theme.colors.textDefault};
`;

const LabelItem = styled.div`
  font-size: 14px;
  text-align: left;
  color: ${({ theme }) => theme.colors.textDefault};
  display: block;
  font-size: 1em;
  margin-top: 0.67em;
  margin-bottom: 0.67em;
  margin-left: 0;
  margin-right: 0;
  font-weight: 550;
`;

const Description = styled.p`
  margin: 0px 0px;
  font-size: 14px;
  line-height: 1.5;
`;

const OptionalText = styled.span`
  color: ${({ theme }) => theme.colors.textDefault};
  font-weight: 400;
`;

const DynamicFormattedMessage = FormattedMessage;

const ReviewOptionText = ({
  isTextArea,
  className,
  isError,
  onChange,
  option,
  rbKey,
  title,
}) => {
  const { formatMessage } = useIntl();
  const [textValue, setTextValue] = useState({
    value: '',
    error: null,
    id: null,
  });

  const checkText = useCallback(
    (mainValue = textValue.value) => {
      if (mainValue && mainValue.replace(/\s/g, '').length) {
        return null;
      } else if (mainValue.replace(/\s/g, '').length) {
        return FIELD_INVALID;
      } else {
        return FIELD_REQUIRED;
      }
    },
    [textValue.value]
  );

  const changeText = useCallback(
    (event) => {
      const { value } = event.target;
      const nextState = {
        value,
        id: option.reviewTemplateFieldId,
        error: null,
      };
      const error = checkText(nextState.value);

      if (error) {
        nextState.error = error;
      }

      setTextValue(nextState);
      onChange(nextState);
    },
    [checkText, onChange, option.reviewTemplateFieldId]
  );

  const handleBlur = useCallback(() => {
    if (textValue.value.length === 0) {
      const error = checkText();
      setTextValue((prevState) => ({ ...prevState, [prevState.error]: error }));
    }
    onChange(textValue);
  }, [checkText, onChange, textValue]);

  const fieldName = isTextArea ? (
    <FormattedMessage
      defaultMessage="YOUR REVIEW"
      id="site_account_reviews.enter_your_review_body"
    />
  ) : (
    <FormattedMessage
      defaultMessage="REVIEW TITLE"
      id="site_account_reviews.enter_your_review_title"
    />
  );

  return (
    <Wrapper className={className} isRequired={option.required}>
      <LabelItem>
        <Title isRequired={option.required}>
          <DynamicFormattedMessage id={rbKey} defaultMessage={title} />
        </Title>
        {option.required ? null : (
          <OptionalText>
            <FormattedMessage
              defaultMessage="(Optional)"
              id="site_account_reviews.reviews_requiredoptional"
            />
          </OptionalText>
        )}
      </LabelItem>
      <FieldWrapper>
        <StyledTextField
          autoComplete="off"
          name={isTextArea ? 'body' : fieldName}
          type="text"
          value={textValue.value}
          tagName={isTextArea ? 'textarea' : 'input'}
          label={isTextArea ? 'Your Review' : null}
          aria-label={formatMessage({
            id: `site_account_reviews_${option.label}`,
            defaultMessage: `Review ${option.label}`,
          })}
          maxLength={2000}
          multiline={!!isTextArea}
          onChange={changeText}
          onBlur={handleBlur}
          isTextArea={!!isTextArea}
          isError={isError}
        />
        <FieldSubText>
          {!isTextArea && (
            <MaximumCharacterMsg>
              <FormattedMessage
                defaultMessage="(Maximum characters: 2000) You have {currentcharlength} character left"
                id="site_account_reviews.reviews_commentlength"
                values={{
                  currentcharlength: 2000 - textValue.value.length,
                }}
              />
            </MaximumCharacterMsg>
          )}
          {isTextArea && (
            <>
              <InstructionsList>
                <InstructionsItem>
                  <FormattedMessage
                    defaultMessage="Focus on the product and your individual experience using it"
                    id="site_account_reviews.review_instructions_1"
                  />
                </InstructionsItem>
                <InstructionsItem>
                  <FormattedMessage
                    defaultMessage="Provide details about why you liked or disliked a product"
                    id="site_account_reviews.review_instructions_2"
                  />
                </InstructionsItem>
                <InstructionsItem>
                  <FormattedMessage
                    defaultMessage="Help others decide if the product will meet their needs"
                    id="site_account_reviews.review_instructions_3"
                  />
                </InstructionsItem>
              </InstructionsList>
              <OptionalHeaderWrapper>
                <OptionalHeader>
                  <FormattedMessage
                    defaultMessage="Help us improve"
                    id="site_account_reviews.reviews_helpusimprove"
                  />
                </OptionalHeader>
                <Description>
                  <FormattedMessage
                    id="site_account_reviews.reviews_helpusfeedback"
                    defaultMessage="We value your opinion. Let us know how we're doing by answering the questions below. The answers to these questions will not appear in your review."
                  />
                </Description>
              </OptionalHeaderWrapper>
            </>
          )}
        </FieldSubText>
      </FieldWrapper>
    </Wrapper>
  );
};

ReviewOptionText.propTypes = {
  className: PropTypes.string,
  isError: PropTypes.bool,
  isTextArea: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  option: PropTypes.object.isRequired,
  rbKey: PropTypes.string,
  title: PropTypes.string.isRequired,
};

export default ReviewOptionText;
